import { useEffect, useState } from 'react';
import { Star, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const TRUSTPILOT_URL = 'https://uk.trustpilot.com/evaluate/r0hub.com';

const lsKeyFor = (userId: string) => `review-prompt-handled:${userId}`;

const hasHandledReviewPrompt = async (userId: string, userEmail?: string | null) => {
  const { data, error } = await (supabase as any).rpc('has_handled_review_prompt', {
    p_user_id: userId,
    p_user_email: userEmail ?? null,
  });

  return { handled: data === true, error };
};

export default function ReviewPromptModal() {
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (loading || !user) return;

    // Only show at least 10 days after sign-up.
    const createdAt = user.created_at ? new Date(user.created_at).getTime() : null;
    if (!createdAt) return;
    if (Date.now() - createdAt < 10 * 24 * 60 * 60 * 1000) return;

    const lsKey = lsKeyFor(user.id);
    try { if (localStorage.getItem(lsKey)) return; } catch { /* noop */ }

    let cancelled = false;
    (async () => {
      // Fail closed: any error or any existing user/email record means the
      // prompt must not open. A dismissed row is permanent across devices.
      const { handled, error } = await hasHandledReviewPrompt(user.id, user.email);
      if (cancelled) return;

      if (error) return;
      if (handled) {
        try { localStorage.setItem(lsKey, '1'); } catch { /* noop */ }
        return;
      }
      setOpen(true);
    })();
    return () => { cancelled = true; };
  }, [user, loading]);

  // Mark this user as permanently handled - write a row and never check again.
  const markHandled = async (payload: { rating?: number; comment?: string; dismissed: boolean }) => {
    if (!user) return;
    try { localStorage.setItem(lsKeyFor(user.id), '1'); } catch { /* noop */ }
    const { error } = await supabase.from('user_reviews').upsert({
      user_id: user.id,
      user_email: user.email ?? null,
      rating: payload.rating ?? null,
      comment: payload.comment ?? null,
      dismissed: payload.dismissed,
    }, { onConflict: 'user_id' });
    if (error) throw error;
  };

  const handleSubmit = async () => {
    if (!user || rating < 1) return;
    setSubmitting(true);
    try {
      await markHandled({ rating, comment: comment.trim() || undefined, dismissed: false });
      setSubmitted(true);
    } catch (e: any) {
      toast.error(e?.message || 'Could not submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTrustpilotClick = async () => {
    if (!submitted) await markHandled({ dismissed: true });
    setOpen(false);
  };

  const handleClose = async (next: boolean) => {
    if (next) return;
    if (!submitted) await markHandled({ dismissed: true });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {submitted ? (
          <>
            <DialogHeader>
              <DialogTitle>Thank you!</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Your feedback has been recorded. We really appreciate you taking the time.
            </p>
            <div className="rounded-lg bg-muted/50 p-4 space-y-3">
              <p className="text-sm">
                If you have a moment, a review on Trustpilot helps R0 Hub reach more students and shows future candidates what to expect.
              </p>
              <a
                href={TRUSTPILOT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full rounded-md text-sm font-medium h-10 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                onClick={handleTrustpilotClick}
              >
                Leave a review on Trustpilot
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Close</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Enjoying R0 Hub? Please leave us a review</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Your feedback helps us improve R0 Hub and guides future students. If you can spare a minute, a Trustpilot review makes a real difference.
            </p>
            <div className="flex items-center gap-1 py-2" onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = (hover || rating) >= n;
                return (
                  <button
                    key={n}
                    type="button"
                    aria-label={`${n} star${n > 1 ? 's' : ''}`}
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHover(n)}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-8 h-8 ${filled ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`}
                    />
                  </button>
                );
              })}
            </div>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Please leave us any feedback or suggestions to improve the platform"
              className="min-h-[100px] placeholder:text-muted-foreground"
              maxLength={2000}
            />
            <div className="flex flex-col gap-3">
              <a
                href={TRUSTPILOT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full rounded-md text-sm font-medium h-10 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                onClick={handleTrustpilotClick}
              >
                Leave a review on Trustpilot
                <ExternalLink className="w-4 h-4" />
              </a>
              <DialogFooter className="w-full sm:justify-center">
                <Button
                  onClick={handleSubmit}
                  disabled={rating < 1 || submitting}
                  variant="outline"
                >
                  {submitting ? 'Submitting…' : 'Send feedback instead'}
                </Button>
              </DialogFooter>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
